import { useId } from 'react';

/**
 * Animated 3D-looking orbital globe used behind the hero.
 * Pure SVG + CSS — no external libs, GPU-accelerated, elegant.
 */
export default function AnimatedGlobe({ size = 640, className = '' }) {
  const raw = useId();
  const uid = raw.replace(/[:]/g, 'x');
  const grad = `og-${uid}`;
  const grad2 = `og2-${uid}`;
  const ring = `ogR-${uid}`;

  return (
    <div
      className={`relative pointer-events-none select-none ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {/* Ambient halo */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(99,102,241,0.35) 0%, rgba(147,51,234,0.18) 30%, transparent 65%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Rotating orbits */}
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 w-full h-full"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={grad} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#c026d3" />
          </linearGradient>
          <linearGradient id={ring} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
            <stop offset="50%" stopColor="#22d3ee" stopOpacity="1" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
          </linearGradient>
          <radialGradient id={grad2} cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="35%" stopColor="#a5b4fc" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#312e81" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Orbit 1 — slow */}
        <g className="orbit-slow" style={{ transformOrigin: '200px 200px' }}>
          <ellipse
            cx="200"
            cy="200"
            rx="180"
            ry="55"
            fill="none"
            stroke={`url(#${ring})`}
            strokeWidth="1.4"
            strokeDasharray="1 4"
            transform="rotate(-25 200 200)"
          />
          {/* Satellite dot */}
          <circle cx="20" cy="200" r="5" fill="#22d3ee" transform="rotate(-25 200 200)">
            <animate attributeName="fill" values="#22d3ee;#a5f3fc;#22d3ee" dur="3s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Orbit 2 — reverse */}
        <g className="orbit-reverse" style={{ transformOrigin: '200px 200px' }}>
          <ellipse
            cx="200"
            cy="200"
            rx="150"
            ry="42"
            fill="none"
            stroke="rgba(196, 181, 253, 0.55)"
            strokeWidth="1.2"
            transform="rotate(15 200 200)"
          />
          <circle cx="50" cy="200" r="4" fill="#c026d3" transform="rotate(15 200 200)" />
          <circle cx="350" cy="200" r="3" fill="#f0abfc" transform="rotate(15 200 200)" />
        </g>

        {/* Orbit 3 — fast */}
        <g className="orbit-fast" style={{ transformOrigin: '200px 200px' }}>
          <ellipse
            cx="200"
            cy="200"
            rx="120"
            ry="30"
            fill="none"
            stroke="rgba(56, 189, 248, 0.35)"
            strokeWidth="1"
            transform="rotate(45 200 200)"
          />
          <circle cx="80" cy="200" r="3.5" fill="#22d3ee" transform="rotate(45 200 200)" />
        </g>

        {/* Central sphere */}
        <g className="globe-breathe" style={{ transformOrigin: '200px 200px' }}>
          <circle cx="200" cy="200" r="72" fill={`url(#${grad})`} />
          <circle cx="200" cy="200" r="72" fill={`url(#${grad2})`} />
          {/* Rim */}
          <circle cx="200" cy="200" r="72" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
          {/* Meridians for 3D feel */}
          <ellipse cx="200" cy="200" rx="72" ry="24" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
          <ellipse cx="200" cy="200" rx="72" ry="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
          <ellipse cx="200" cy="200" rx="24" ry="72" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
          <ellipse cx="200" cy="200" rx="45" ry="72" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
        </g>
      </svg>
    </div>
  );
}
